import axiosInstance from '../axiosInstance';
import { initializeDB } from '../config/db';
import { VESSEL_HYDRO_TEST_STORE } from '../config/constants';

const vesselHydroTestService = {
    /**
     * Submit vessel hydro test form to API
     * @param {object} formData
     * @returns {Promise<{success: boolean, message?: string, id?: number, uuid?: string}>}
     */
    async submit(formData) {
        try {
            const submitData = {
                ...formData,
                status: 'submitted',
                updatedAt: new Date().toISOString()
            };

            // Call API
            const response = await axiosInstance.post('/vessel-hydro-test/submit', submitData);

            if (response.data.success) {
                // Save to IndexedDB
                const db = await initializeDB();
                if (db.objectStoreNames.contains(VESSEL_HYDRO_TEST_STORE)) {
                    const tx = db.transaction(VESSEL_HYDRO_TEST_STORE, 'readwrite');
                    const store = tx.objectStore(VESSEL_HYDRO_TEST_STORE);
                    await store.put(submitData);
                }

                return {
                    success: true,
                    id: response.data.id,
                    uuid: response.data.uuid,
                    message: response.data.message || 'Form submitted successfully'
                };
            } else {
                throw new Error(response.data.message || 'Failed to submit form');
            }
        } catch (error) {
            console.error('Error submitting vessel hydro test:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to submit form'
            };
        }
    },

    /**
     * Save draft to IndexedDB
     * @param {object} formData
     * @returns {Promise<void>}
     */
    async saveDraft(formData) {
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(VESSEL_HYDRO_TEST_STORE)) {
                const tx = db.transaction(VESSEL_HYDRO_TEST_STORE, 'readwrite');
                const store = tx.objectStore(VESSEL_HYDRO_TEST_STORE);
                const saveData = {
                    ...formData,
                    status: 'draft',
                    updatedAt: new Date().toISOString()
                };
                await store.put(saveData);
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            throw error;
        }
    },

    /**
     * Load form by UUID from IndexedDB
     * @param {string} uuid
     * @returns {Promise<object|null>}
     */
    async loadByUuid(uuid) {
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(VESSEL_HYDRO_TEST_STORE)) {
                const tx = db.transaction(VESSEL_HYDRO_TEST_STORE, 'readonly');
                const store = tx.objectStore(VESSEL_HYDRO_TEST_STORE);
                return await store.get(uuid);
            }
            return null;
        } catch (error) {
            console.error('Error loading form:', error);
            return null;
        }
    },

    /**
     * Get all forms from IndexedDB
     * @returns {Promise<array>}
     */
    async getAll() {
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(VESSEL_HYDRO_TEST_STORE)) {
                const tx = db.transaction(VESSEL_HYDRO_TEST_STORE, 'readonly');
                const store = tx.objectStore(VESSEL_HYDRO_TEST_STORE);
                const allForms = await store.getAll();
                // Sort by updatedAt descending (newest first)
                return (allForms || []).sort((a, b) => {
                    const dateA = new Date(a.updatedAt || a.createdAt || 0);
                    const dateB = new Date(b.updatedAt || b.createdAt || 0);
                    return dateB - dateA;
                });
            }
            return [];
        } catch (error) {
            console.error('Error getting all forms:', error);
            return [];
        }
    },

    /**
     * Delete form from IndexedDB
     * @param {string} uuid
     * @returns {Promise<void>}
     */
    async delete(uuid) {
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(VESSEL_HYDRO_TEST_STORE)) {
                const tx = db.transaction(VESSEL_HYDRO_TEST_STORE, 'readwrite');
                const store = tx.objectStore(VESSEL_HYDRO_TEST_STORE);
                await store.delete(uuid);
            }
        } catch (error) {
            console.error('Error deleting form:', error);
            throw error;
        }
    }
};

export default vesselHydroTestService;
